/**
 * Desk name tags — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_classroom`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const nameTagsPack: ToilePack = {
    slug: 'name-tags',
    category: 'classroom',
    name: 'Desk name tags',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'dino', label: 'Dinosaur', subject: 'a friendly green T-rex at one end and a small volcano at the other, palm fronds along the edges'},
        {key: 'rainbow', label: 'Rainbow', subject: 'a rainbow arc at one end and two round clouds at the other, small stars scattered along the edges'},
        {key: 'rocket', label: 'Rocket', subject: 'a rocket at one end and a ringed planet at the other, tiny stars trailing along the edges'},
    ],
    styles: [
        {key: 'crayon', label: 'Crayon', style: 'bright kindergarten marker art, thick rounded outlines and simple flat fills', palette: 'primary red, yellow, green and blue on white', framing: 'The tags stacked flat and square on the page, seen straight on.'},
        {key: 'pastel', label: 'Pastel', style: 'soft watercolor illustration, gentle washes over a light pencil outline', palette: 'blush, mint and pale butter yellow on cream paper', framing: 'The tags laid out flat, a wide quiet margin down each side of the page.'},
        {key: 'modern', label: 'Modern', style: 'flat modern vector design, thin even outlines and simple geometric shapes', palette: 'slate blue, mustard and warm grey on white', framing: 'The tags aligned flat on a plain page, a thin rule framing each one.'},
    ],
    directions: ['with a number line instead', 'for a preschool room', 'as library bookmarks'],
    seo: {
        title: 'Desk name tags: dinosaur, rainbow, rocket printable',
        metaDescription: 'Nine desk name tags — dinosaur, rainbow and rocket — in crayon, pastel and modern styles, eight to a sheet with cut lines.',
        h1: 'Desk name tags: dinosaur, rainbow and rocket in three styles',
        tagline: 'Nine name tags, three motifs, three looks — eight to a sheet with cut lines.',
        intro: "The strip taped to the front edge of every desk on day one: a blank line for the child's name, a small alphabet or number line, a motif at each end. These nine print eight to a sheet with cut lines, in three motifs — dinosaur, rainbow, rocket — and three looks, from crayon-bright to clean and modern. Type a different animal or object and the set redraws the same way.",
        useCases: [
            'desk name tags for the first week',
            'a name-tag swap when seats change',
            'a cubby or coat-hook label',
            'a name-tag for a school event',
        ],
        faq: [
            {q: 'How many name tags print per sheet?', a: 'Eight per sheet with cut lines, so one print run covers a small class and a few spares for the seat swap.'},
            {q: 'Can I get a different motif?', a: 'Yes — type a different animal or object and the eight-tag sheet redraws in the same three styles.'},
            {q: 'What size are the individual tags?', a: 'Sized to cut and fit a standard desk edge once separated along the printed cut lines.'},
        ],
    },
}
